import React, { useEffect, useId, useMemo, useRef, useState } from 'react';

const US_STATE_ABBREVS = {
  alabama: 'AL',
  alaska: 'AK',
  arizona: 'AZ',
  arkansas: 'AR',
  california: 'CA',
  colorado: 'CO',
  connecticut: 'CT',
  delaware: 'DE',
  florida: 'FL',
  georgia: 'GA',
  hawaii: 'HI',
  idaho: 'ID',
  illinois: 'IL',
  indiana: 'IN',
  iowa: 'IA',
  kansas: 'KS',
  kentucky: 'KY',
  louisiana: 'LA',
  maine: 'ME',
  maryland: 'MD',
  massachusetts: 'MA',
  michigan: 'MI',
  minnesota: 'MN',
  mississippi: 'MS',
  missouri: 'MO',
  montana: 'MT',
  nebraska: 'NE',
  nevada: 'NV',
  'new hampshire': 'NH',
  'new jersey': 'NJ',
  'new mexico': 'NM',
  'new york': 'NY',
  'north carolina': 'NC',
  'north dakota': 'ND',
  ohio: 'OH',
  oklahoma: 'OK',
  oregon: 'OR',
  pennsylvania: 'PA',
  'rhode island': 'RI',
  'south carolina': 'SC',
  'south dakota': 'SD',
  tennessee: 'TN',
  texas: 'TX',
  utah: 'UT',
  vermont: 'VT',
  virginia: 'VA',
  washington: 'WA',
  'west virginia': 'WV',
  wisconsin: 'WI',
  wyoming: 'WY',
  'district of columbia': 'DC',
  'washington, d.c.': 'DC',
  'washington, dc': 'DC',
  'washington, d.c': 'DC',
};

export function formatIpCount(value) {
  const count = Number(value);
  if (!Number.isFinite(count)) return '';
  return `${count.toLocaleString('en-US')} provider-listed IPs`;
}

export function formatStateAbbrev(name) {
  const raw = String(name || '').trim();
  if (!raw) return '';
  if (/^[A-Za-z]{2}$/.test(raw)) return raw.toUpperCase();
  return US_STATE_ABBREVS[raw.toLowerCase()] || raw;
}

export function formatQaTargetingOptionLabel(kind, item, { allLabel = '' } = {}) {
  if (!item || item.isAll) return allLabel;
  const ipCount = Number(item.availableIpCount);
  const hasZeroIps = Number.isFinite(ipCount) && ipCount <= 0;
  const ip = formatIpCount(item.availableIpCount);
  if (kind === 'zip') {
    const geo = item.geography || {};
    const city = String(geo.city || '').trim();
    const state = formatStateAbbrev(geo.state);
    const geoPart = [city, state].filter(Boolean).join(', ');
    const zip = item.providerCode || item.displayName || '';
    if (hasZeroIps || item.providerSupported === false) {
      return `${zip} — Not currently available from provider`;
    }
    if (geoPart && ip) return `${zip} — ${geoPart} · ${ip}`;
    if (geoPart) return `${zip} — ${geoPart}`;
    if (ip) return `${zip} · ${ip}`;
    return String(zip);
  }
  if (kind === 'asn') {
    const code = item.providerCode || item.displayName || '';
    const providerName = String(item.providerName || '').trim();
    const namePart = providerName && providerName !== code ? ` · ${providerName}` : '';
    return ip ? `${code}${namePart} · ${ip}` : `${code}${namePart}`;
  }
  const name = item.displayName || item.providerCode || '';
  if (hasZeroIps || item.providerSupported === false) {
    return `${name} — Not currently available from provider`;
  }
  return ip ? `${name} · ${ip}` : String(name);
}

export default function QaTargetingCatalogSearch({
  label,
  kind,
  testId,
  value,
  allValue,
  allLabel,
  disabled = false,
  placeholder = 'Search',
  selectedItem = null,
  onSelect,
  onSearch,
  emptyMessage = 'No matching results.',
}) {
  const listId = useId();
  const inputId = useId();
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(null);
  const [searchPending, setSearchPending] = useState(false);

  const isAll = !value || value === allValue;
  const selected = isAll
    ? { providerCode: allValue, displayName: allLabel, isAll: true }
    : (selectedItem || { providerCode: value, displayName: value });
  const displayValue = formatQaTargetingOptionLabel(kind, selected, { allLabel });

  useEffect(() => {
    if (disabled || !open) return undefined;
    const needle = String(query || '').trim();
    if (!needle) {
      setResults([]);
      setLoading(false);
      setSearchPending(false);
      return undefined;
    }
    let cancelled = false;
    setSearchPending(true);
    setActiveIndex(null);
    const handle = setTimeout(async () => {
      setLoading(true);
      try {
        const payload = await onSearch(needle);
        if (cancelled) return;
        const rows = Array.isArray(payload) ? payload : payload?.results || [];
        setResults(rows);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) {
          setLoading(false);
          setSearchPending(false);
        }
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [disabled, open, query, onSearch]);

  useEffect(() => {
    if (!open) return undefined;
    const onMouseDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open]);

  const options = useMemo(() => ([
    { providerCode: allValue, displayName: allLabel, availableIpCount: null, isAll: true },
    ...results,
  ]), [allLabel, allValue, results]);

  const closeAndSelect = (item) => {
    onSelect(item?.isAll ? allValue : item.providerCode, item?.isAll ? null : item);
    setOpen(false);
    setQuery('');
    setResults([]);
    setActiveIndex(null);
    setSearchPending(false);
  };

  const onKeyDown = (event) => {
    if (disabled) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
      setQuery('');
      setActiveIndex(null);
      setSearchPending(false);
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        setQuery('');
        return;
      }
      setActiveIndex((current) => {
        if (current == null) return 0;
        return Math.min(current + 1, Math.max(options.length - 1, 0));
      });
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) return;
      setActiveIndex((current) => {
        if (current == null) return Math.max(options.length - 1, 0);
        return Math.max(current - 1, 0);
      });
      return;
    }
    if (event.key === 'Enter') {
      if (!open) return;
      if (activeIndex != null && !searchPending && !loading) {
        const item = options[activeIndex];
        if (item) {
          event.preventDefault();
          closeAndSelect(item);
        }
        return;
      }
      if (searchPending || loading || String(query || '').trim()) {
        event.preventDefault();
      }
    }
  };

  return (
    <div className="fg-qa-catalog-search" ref={rootRef} data-kind={kind}>
      <label htmlFor={inputId}>{label}</label>
      <div className="fg-qa-catalog-search__control">
        <input
          id={inputId}
          className="fg-application-input"
          data-testid={testId}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={listId}
          aria-haspopup="listbox"
          autoComplete="off"
          disabled={disabled}
          placeholder={placeholder}
          value={open ? query : displayValue}
          onFocus={() => {
            if (disabled) return;
            setOpen(true);
            setQuery('');
            setActiveIndex(null);
          }}
          onChange={(event) => {
            const nextQuery = event.target.value;
            setOpen(true);
            setQuery(nextQuery);
            setActiveIndex(null);
            setSearchPending(Boolean(String(nextQuery || '').trim()));
          }}
          onKeyDown={onKeyDown}
        />
        {!isAll && !disabled ? (
          <button
            type="button"
            className="fg-qa-catalog-search__clear"
            data-testid={`${testId}-clear`}
            aria-label="Clear"
            onClick={() => closeAndSelect({ isAll: true, providerCode: allValue, displayName: allLabel })}
          >
            ×
          </button>
        ) : null}
      </div>
      {open && !disabled ? (
        <ul
          id={listId}
          className="fg-qa-catalog-search__list"
          role="listbox"
          aria-multiselectable="false"
          data-testid={`${testId}-results`}
        >
          {loading ? <li className="fg-qa-catalog-search__empty">Searching…</li> : null}
          {!loading && String(query || '').trim() && results.length === 0 ? (
            <li className="fg-qa-catalog-search__empty" data-testid={`${testId}-empty`}>
              {kind === 'zip' && /^\d{5}$/.test(String(query || '').trim())
                ? `${String(query).trim()} — Not currently available from provider`
                : emptyMessage}
            </li>
          ) : null}
          {options.map((item, index) => {
            const optionId = `${listId}-option-${item.providerCode || index}`;
            return (
              <li key={`${item.providerCode || 'all'}-${index}`}>
                <button
                  type="button"
                  id={optionId}
                  role="option"
                  className="fg-qa-catalog-search__option"
                  aria-selected={activeIndex != null && index === activeIndex}
                  data-testid={`${testId}-option-${item.isAll ? 'all' : item.providerCode}`}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => closeAndSelect(item)}
                >
                  {formatQaTargetingOptionLabel(kind, item, { allLabel })}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
